import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Menu, X, ArrowUpRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { benchmarkUrl } from '../lib/constants/benchmark'
import { ThemeToggle } from './ThemeToggle'
import { LanguageSwitcher } from './LanguageSwitcher'
import { useTheme } from './ThemeProvider'
import { isOfflineModeActive } from '../config/offlineAuth'
import { useBackendHealth } from '../lib/backendHealth'
import { API_BASE_URL } from '../lib/api'

interface HeaderProps {
  onNavigate?: (sectionId: string) => void
}

interface UserData {
  id: string
  email: string
  name: string
  role: string
}

export function Header({ onNavigate }: HeaderProps) {
  const { t, i18n } = useTranslation('home')
  const navigate = useNavigate()
  const { theme } = useTheme()
  const [isScrolled, setIsScrolled] = useState(false)
  const [activeSection, setActiveSection] = useState('hero')
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [_user, setUser] = useState<UserData | null>(null)
  // const [isUserMenuOpen, setIsUserMenuOpen] = useState(false) // disabled with user menu
  const [isOffline, setIsOffline] = useState(false)

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isMobileMenuOpen])

  // Check backend health for news availability
  const { isAvailable: isBackendAvailable, isChecking: isCheckingBackend } = useBackendHealth(API_BASE_URL, {
    enabled: true,
  })

  // Check if user is logged in on mount and when storage changes
  useEffect(() => {
    const checkAuth = () => {
      const userStr = localStorage.getItem('user')
      if (userStr) {
        try {
          setUser(JSON.parse(userStr))
        } catch {
          setUser(null)
        }
      } else {
        setUser(null)
      }

      // Check offline mode status
      setIsOffline(isOfflineModeActive())
    }

    checkAuth()

    // Listen for storage changes (for login/logout in other tabs)
    window.addEventListener('storage', checkAuth)
    // Also listen for custom auth events
    window.addEventListener('auth-change', checkAuth)

    return () => {
      window.removeEventListener('storage', checkAuth)
      window.removeEventListener('auth-change', checkAuth)
    }
  }, [])

  // handleLogout disabled along with user menu
  /* const handleLogout = () => {
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    if (isOfflineModeActive()) {
      clearOfflineAuth()
    }
    setUser(null)
    setIsOffline(false)
    setIsUserMenuOpen(false)
    window.dispatchEvent(new Event('auth-change'))
  } */

  const navItems = [
    { id: 'hero', label: t('navigation.home') },
    { id: 'demo', label: t('navigation.demo') },
    { id: 'how-it-works', label: t('navigation.howItWorks') },
    { id: 'use-cases', label: t('navigation.useCases') },
    { id: 'aria', label: 'ARIA' },
    { id: 'about', label: t('navigation.about') },
    { id: 'team', label: t('navigation.team') },
    { id: 'news', label: t('navigation.news') },
  ].filter(item => {
    // Hide News section when offline, while checking backend, or when backend is unavailable
    if (item.id === 'news' && (isOffline || isCheckingBackend || isBackendAvailable === false)) {
      return false
    }
    return true
  })

  // Handle scroll detection for header background
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Handle active section detection
  useEffect(() => {
    const handleScroll = () => {
      // Check for sections that exist on the current page (exclude news which is a separate route)
      const sections = navItems
        .filter(item => item.id !== 'news')
        .map(item => document.getElementById(item.id))
        .filter((el): el is HTMLElement => el !== null)

      for (const section of sections) {
        const rect = section.getBoundingClientRect()
        if (rect.top <= 100 && rect.bottom >= 100) {
          setActiveSection(section.id)
          break
        }
      }
    }

    window.addEventListener('scroll', handleScroll)
    handleScroll() // Initial check
    return () => window.removeEventListener('scroll', handleScroll)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Smooth scroll to section
  const handleNavClick = (sectionId: string) => {
    // Special handling for news which has its own route
    if (sectionId === 'news') {
      const currentLang = window.location.pathname.split('/')[1] || 'en'
      setIsMobileMenuOpen(false)
      navigate(`/${currentLang}/${sectionId}`)
      return
    }

    // For 'hero', scroll to top of page
    if (sectionId === 'hero') {
      setIsMobileMenuOpen(false)
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' })
        setActiveSection('hero')
        onNavigate?.(sectionId)
      }, 100)
      return
    }

    // Check if the section exists on current page
    const section = document.getElementById(sectionId)

    if (section) {
      // Section exists - scroll to it
      setIsMobileMenuOpen(false)

      setTimeout(() => {
        const headerHeight = 80
        const elementPosition = section.getBoundingClientRect().top
        const offsetPosition = elementPosition + window.pageYOffset - headerHeight

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        })

        setActiveSection(sectionId)
        onNavigate?.(sectionId)
      }, 100)
    } else {
      // Section doesn't exist on current page - navigate to main page with hash
      const currentLang = window.location.pathname.split('/')[1] || 'en'
      setIsMobileMenuOpen(false)
      navigate(`/${currentLang}#${sectionId}`)
    }
  }

  return (
    <>
      {/* Desktop & Tablet Header */}
      <motion.header
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
          isScrolled
            ? 'bg-gray-900/80 backdrop-blur-md shadow-md'
            : (theme === 'dark' ? 'bg-gray-900' : 'bg-white')
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <nav className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            {/* Logo */}
            <motion.div
              className="flex-shrink-0 flex items-center gap-2"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <a
                href="#hero"
                onClick={(e) => {
                  e.preventDefault()
                  handleNavClick('hero')
                }}
                className="flex items-center gap-2"
              >
                <img
                  src={isScrolled || theme === 'dark'
                    ? "/logo_transparent_eye.png"
                    : "/logo_transparent_eye_dark.png"}
                  alt="securesight.ai Logo"
                  className="h-8 sm:h-10 w-auto object-contain"
                />
                <span className={`text-2xl sm:text-3xl font-bold bg-gradient-to-r bg-clip-text text-transparent ${
                  isScrolled
                    ? 'from-orange-400 to-red-400'
                    : 'from-orange-500 to-red-500'
                }`}
                >
                  securesight.ai
                </span>
              </a>
            </motion.div>

            {/* Desktop Navigation – erst ab xl (1280px): darunter passt die Zeile mit
                acht Punkten plus Sprache/Theme nicht, dort greift das Mobile-Menü */}
            <div className="hidden xl:flex items-center space-x-1">
              {navItems.map((item) => (
                <motion.button
                  key={item.id}
                  onClick={() => handleNavClick(item.id)}
                  className={`px-2.5 2xl:px-4 py-2 rounded-lg text-base font-medium whitespace-nowrap transition-all duration-200 relative ${
                    activeSection === item.id
                      ? (isScrolled || theme === 'dark' ? 'text-orange-400' : 'text-orange-500')
                      : (isScrolled || theme === 'dark' ? 'text-gray-300 hover:text-orange-400' : 'text-black hover:text-orange-500')
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {item.label}
                  {activeSection === item.id && (
                    <motion.div
                      layoutId="activeSection"
                      className={`absolute bottom-0 left-0 right-0 h-0.5 transition-colors ${
                        activeSection === item.id
                          ? (isScrolled || theme === 'dark' ? 'bg-orange-400' : 'bg-orange-500')
                          : ''
                      }`}
                      transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                    />
                  )}
                </motion.button>
              ))}
              {/* Benchmark: eigene Seite (benchmark.securesight.ai), deshalb ein Link
                  mit Pfeil statt eines Sprungziels auf dieser Seite */}
              <motion.a
                href={benchmarkUrl(i18n.language)}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t('navigation.benchmarkAria')}
                className={`inline-flex items-center gap-1 px-2.5 2xl:px-4 py-2 rounded-lg text-base font-medium whitespace-nowrap transition-all duration-200 ${
                  isScrolled || theme === 'dark' ? 'text-gray-300 hover:text-orange-400' : 'text-black hover:text-orange-500'
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {t('navigation.benchmark')}
                <ArrowUpRight size={14} aria-hidden="true" className="opacity-70" />
              </motion.a>
              {/* Language Switcher */}
              <div className="ml-2">
                <LanguageSwitcher />
              </div>
              {/* Theme Toggle */}
              <div className="ml-2">
                <ThemeToggle />
              </div>

              {/* Login / User Menu - DISABLED */}
              {/* <div className="ml-2 relative">
                {user ? (
                  <div className="relative">
                    <motion.button
                      onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors border ${
                        isScrolled
                          ? 'text-gray-300 hover:bg-gray-800 border-gray-700'
                          : 'text-gray-900 hover:bg-gray-100 border-gray-300'
                      }`}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <User size={18} />
                      <span className="hidden lg:inline">{user.name || user.email}</span>
                      <ChevronDown
                        size={16}
                        className={`transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`}
                      />
                    </motion.button>

                    <AnimatePresence>
                      {isUserMenuOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.15 }}
                          className="absolute right-0 mt-2 w-48 bg-card rounded-lg shadow-lg border border-border py-1 z-50"
                        >
                          <motion.button
                            onClick={() => {
                              setIsUserMenuOpen(false)
                              navigate('/dashboard')
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-muted transition-colors flex items-center gap-2"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            {t('navigation.dashboard')}
                          </motion.button>
                          <motion.button
                            onClick={() => {
                              setIsUserMenuOpen(false)
                              navigate('/settings')
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-muted transition-colors flex items-center gap-2"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            {t('navigation.settings')}
                          </motion.button>
                          <hr className="my-1 border-gray-200" />
                          <motion.button
                            onClick={handleLogout}
                            className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            <LogOut size={16} />
                            {t('navigation.logout')}
                          </motion.button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <motion.button
                    onClick={() => navigate('/login')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors text-white ${
                      isScrolled ? 'bg-orange-500 hover:bg-orange-600' : 'bg-orange-600 hover:bg-orange-700'
                    }`}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <LogIn size={18} />
                    <span className="hidden lg:inline">{t('navigation.login')}</span>
                  </motion.button>
                )}
              </div> */}
            </div>

            {/* Mobile Menu Button */}
            <div className="xl:hidden">
              <button
                type="button"
                onClick={() => setIsMobileMenuOpen(prev => !prev)}
                className={`p-2 rounded-lg transition-colors touch-manipulation cursor-pointer ${
                  isScrolled || theme === 'dark' ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-900 hover:bg-gray-100'
                }`}
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </nav>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              className={`xl:hidden backdrop-blur-md border-t transition-colors ${
                isScrolled || theme === 'dark' ? 'bg-gray-900/95 border-gray-700' : 'bg-white/95 border-gray-200'
              }`}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
            >
              <nav className="container mx-auto px-4 py-4 space-y-2">
                {navItems.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => handleNavClick(item.id)}
                    className={`block w-full text-left px-4 py-3 rounded-lg text-base font-medium transition-all duration-200 touch-manipulation cursor-pointer ${
                      activeSection === item.id
                        ? (isScrolled || theme === 'dark' ? 'bg-orange-900/30 text-orange-400' : 'bg-orange-50 text-orange-500')
                        : (isScrolled || theme === 'dark' ? 'text-gray-300 hover:bg-gray-800' : 'text-black hover:bg-gray-100')
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
                <a
                  href={benchmarkUrl(i18n.language)}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={t('navigation.benchmarkAria')}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`flex w-full items-center gap-1.5 px-4 py-3 rounded-lg text-base font-medium transition-all duration-200 touch-manipulation ${
                    isScrolled || theme === 'dark' ? 'text-gray-300 hover:bg-gray-800' : 'text-black hover:bg-gray-100'
                  }`}
                >
                  {t('navigation.benchmark')}
                  <ArrowUpRight size={16} aria-hidden="true" className="opacity-70" />
                </a>

                {/* Theme Toggle and Language Switcher in Mobile */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: navItems.length * 0.05 }}
                  className={`flex items-center gap-3 px-4 py-3 border-t transition-colors ${
                    isScrolled || theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                  }`}
                >
                  <LanguageSwitcher />
                  <ThemeToggle />
                </motion.div>

                {/* Login / User Menu in Mobile - DISABLED */}
                {/* <div
                  className={`px-4 py-3 border-t transition-colors ${
                    isScrolled ? 'border-gray-700' : 'border-gray-200'
                  }`}
                >
                  {user ? (
                    <div className="space-y-2">
                      <div className={`flex items-center gap-2 text-base font-medium transition-colors ${
                        isScrolled ? 'text-gray-300' : 'text-foreground'
                      }`}>
                        <User size={18} />
                        <span>{user.name || user.email}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setIsMobileMenuOpen(false)
                          navigate('/dashboard')
                        }}
                        className={`block w-full text-left px-4 py-2 rounded-lg transition-colors touch-manipulation cursor-pointer ${
                          isScrolled ? 'text-gray-300 hover:bg-gray-800' : 'text-foreground hover:bg-gray-100'
                        }`}
                      >
                        {t('navigation.dashboard')}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setIsMobileMenuOpen(false)
                          navigate('/settings')
                        }}
                        className={`block w-full text-left px-4 py-2 rounded-lg transition-colors touch-manipulation cursor-pointer ${
                          isScrolled ? 'text-gray-300 hover:bg-gray-800' : 'text-foreground hover:bg-gray-100'
                        }`}
                      >
                        {t('navigation.settings')}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          handleLogout()
                          setIsMobileMenuOpen(false)
                        }}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors touch-manipulation cursor-pointer ${
                          isScrolled ? 'text-red-400 hover:bg-red-900/20' : 'text-red-600 hover:bg-red-50'
                        }`}
                      >
                        <LogOut size={18} />
                        {t('navigation.logout')}
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setIsMobileMenuOpen(false)
                        navigate('/login')
                      }}
                      className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-base font-medium transition-colors text-white touch-manipulation cursor-pointer ${
                        isScrolled ? 'bg-orange-500' : 'bg-orange-600'
                      }`}
                    >
                      <LogIn size={18} />
                      {t('navigation.login')}
                    </button>
                  )}
                </div> */}
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>

      {/* Spacer for fixed header */}
      <div className="h-16 sm:h-20" />
    </>
  )
}
